﻿import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { APP_REGISTRY } from './apps/appRegistry';
import { isPlatformAnalyticsAppId } from './apps/platformAnalytics';
import { HUB_REGISTRY } from './apps/registry/hubRegistry';
import { useSiteCapabilitiesStore } from './site/useSiteCapabilitiesStore';
import { AuthBootstrap } from './auth/AuthBootstrap';
import { GuestOnly } from './auth/GuestOnly';
import { RequireAuth } from './auth/RequireAuth';
import { RequireMediaLibrary } from './auth/RequireMediaLibrary';
import { RequireRole } from './auth/RequireRole';
import { MEDIA_LIBRARY_APP_ID, type AssistantRole } from './auth/roles';
import { AppShell } from './components/AppShell/AppShell';
import { EditorSessionLayout, OpenByStoragePage } from '@jarvis/platform-ui';
import { AccessDeniedPage } from './pages/AccessDenied/AccessDeniedPage';
import { UserActivityHistoryPage } from './pages/Admin/UserActivity/UserActivityHistoryPage';
import { UserActivitySummaryPage } from './pages/Admin/UserActivity/UserActivitySummaryPage';
import { AppStorePage } from './pages/Apps/AppStore/AppStorePage';
import { DashboardPage } from './pages/Dashboard/DashboardPage';
import { LoginPage } from './pages/Login/LoginPage';
import { SettingsAboutPage } from './pages/Settings/SettingsAboutPage';
import { SettingsAppearancePage } from './pages/Settings/SettingsAppearancePage';
import { SettingsEditorsPage } from './pages/Settings/SettingsEditorsPage';
import { SettingsGeneralPage } from './pages/Settings/SettingsGeneralPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { SettingsStoragePage } from './pages/Settings/SettingsStoragePage';
import { SettingsSupportPage } from './pages/Settings/SettingsSupportPage';
import { UserProfilePage } from './pages/Settings/UserProfilePage';
import { LegalMarkdownPage } from '@jarvis/platform-ui';
import { AdminBugsPage } from './pages/Admin/Bugs/AdminBugsPage';
import { AdminFeedbackPage } from './pages/Admin/Feedback/AdminFeedbackPage';
import { SupportMetadataAdminPage } from './pages/Admin/SupportMetadata/SupportMetadataAdminPage';
import { MyTicketsPage } from './pages/MyTickets/MyTicketsPage';
import { StorageMountsAdminPage } from './pages/Admin/StorageMounts/StorageMountsAdminPage';
import { StorageFileAuditPage } from './pages/Admin/StorageFileAudit/StorageFileAuditPage';
import { MediaLibraryMetadataPage } from './pages/Admin/MediaLibrary/MediaLibraryMetadataPage';
import { AsciiArtAdminPage } from './pages/Admin/AsciiArt/AsciiArtAdminPage';
import { CustomAppsAdminPage } from './pages/Admin/CustomApps/CustomAppsAdminPage';
import { EmojiLibraryAdminPage } from './pages/Admin/EmojiLibrary/EmojiLibraryAdminPage';
import { GifLibraryAdminPage } from './pages/Admin/GifLibrary/GifLibraryAdminPage';
import { OpenMetadataAdminPage } from './pages/Admin/OpenMetadata/OpenMetadataAdminPage';
import { CustomAppLaunchPage } from './pages/Apps/CustomAppLaunchPage';
import { FilesPage } from './pages/Files/FilesPage';
import { ImageViewerPage } from './pages/Files/ImageViewerPage';
import { VideoViewerPage } from './pages/Files/VideoViewerPage';
import { SpreadsheetEditorPage } from './pages/Files/SpreadsheetEditorPage';
import { WordEditorPage } from './pages/Files/WordEditorPage';
import { DocumentPreviewPage } from './pages/Files/DocumentPreviewPage';
import { PdfViewerPage } from './pages/Files/PdfViewerPage';
import { TextEditorPage } from './pages/Files/TextEditorPage';
import { MarkdownEditorPage } from './pages/Files/MarkdownEditorPage';
import { DrawioEditorPage } from './pages/Files/DrawioEditorPage';
import { WorkspacePage } from './pages/Files/WorkspacePage';
import { FileAccessRestrictedPage } from './pages/Files/FileAccessRestrictedPage';

/** Lazy-load Code Editor so Monaco/workers/formatters are not on the main bundle. */
const CodeEditorPage = lazy(() =>
  import('./pages/Files/CodeEditorPage').then((m) => ({ default: m.CodeEditorPage }))
);

function LazyAppRoute({
  appId,
  minRole,
  Component
}: {
  appId: string;
  minRole: AssistantRole;
  Component: ComponentType;
}) {
  const page = (
    <Suspense fallback={<div style={{ padding: '1.5rem' }}>Loading app…</div>}>
      <Component />
    </Suspense>
  );
  // Media Library is Admin|SysAdmin only (excludes DevOps elevated access).
  if (appId === MEDIA_LIBRARY_APP_ID) {
    return <RequireMediaLibrary>{page}</RequireMediaLibrary>;
  }
  // User-tier: any Assistant role (RequireAuth already ensures sign-in).
  // Elevated / SysAdmin: RequireRole + hasAssistantRole expansion.
  if (minRole === 'AssistantUser') {
    return page;
  }
  return <RequireRole role={minRole}>{page}</RequireRole>;
}

const HUB_APP_IDS = new Set(HUB_REGISTRY.map((hub) => hub.id));
const NESTED_APP_IDS = new Set<string>([
  'data-sources',
  'data-workspace',
  'dashboard-builder',
  'report-builder',
  'analytics-library'
]);

export default function App() {
  const analyticsEnabled = useSiteCapabilitiesStore((s) => s.analyticsEnabled);

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AuthBootstrap />}>
          <Route
            path="/login"
            element={
              <GuestOnly>
                <LoginPage />
              </GuestOnly>
            }
          />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route
            element={
              <RequireAuth>
                <EditorSessionLayout shell={AppShell} />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            {APP_REGISTRY.filter((app) => analyticsEnabled || !isPlatformAnalyticsAppId(app.id)).map((app) => (
              <Route
                key={app.id}
                path={
                  HUB_APP_IDS.has(app.id) || NESTED_APP_IDS.has(app.id)
                    ? `${app.path.replace(/^\//, '')}/*`
                    : app.path.replace(/^\//, '')
                }
                element={
                  <LazyAppRoute appId={app.id} minRole={app.minRole} Component={app.component} />
                }
              />
            ))}
            <Route path="files" element={<FilesPage />} />
            <Route path="files/open-by-storage" element={<OpenByStoragePage filesLinkTo="/files" filesLinkLabel="Files" />} />
            <Route path="files/access-restricted" element={<FileAccessRestrictedPage />} />
            <Route path="files/image-viewer" element={<ImageViewerPage />} />
            <Route path="files/video-viewer" element={<VideoViewerPage />} />
            <Route path="files/spreadsheet-editor" element={<SpreadsheetEditorPage />} />
            <Route path="files/document-editor" element={<WordEditorPage />} />
            <Route path="files/document-preview" element={<DocumentPreviewPage />} />
            <Route path="files/pdf-viewer" element={<PdfViewerPage />} />
            <Route path="files/text-editor" element={<TextEditorPage />} />
            <Route
              path="files/code-editor"
              element={
                <Suspense fallback={<div style={{ padding: '1.5rem' }}>Loading Code Editor…</div>}>
                  <CodeEditorPage />
                </Suspense>
              }
            />
            <Route path="files/markdown-editor" element={<MarkdownEditorPage />} />
            <Route path="files/drawio-editor" element={<DrawioEditorPage />} />
            <Route path="files/workspace" element={<WorkspacePage />} />
            <Route
              path="admin/user-activity/history"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <UserActivityHistoryPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/user-activity"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <UserActivitySummaryPage />
                </RequireRole>
              }
            />
            <Route path="my-tickets" element={<MyTicketsPage />} />
            <Route
              path="admin/bugs"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <AdminBugsPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/feedback"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <AdminFeedbackPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/support-metadata"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <SupportMetadataAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/storage-mounts"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <StorageMountsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/storage-file-audit"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <StorageFileAuditPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/media-library-config"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <MediaLibraryMetadataPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/media-library-metadata"
              element={<Navigate to="/admin/media-library-config" replace />}
            />
            <Route
              path="admin/ascii-art"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <AsciiArtAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/emoji-library"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <EmojiLibraryAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/gif-library"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <GifLibraryAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/custom-apps"
              element={
                <RequireRole role="AssistantSysAdmin">
                  <CustomAppsAdminPage />
                </RequireRole>
              }
            />
            <Route path="apps/link/:slug" element={<CustomAppLaunchPage />} />
            <Route
              path="admin/openmetadata"
              element={
                analyticsEnabled ? (
                  <RequireRole role="AssistantSysAdmin">
                    <OpenMetadataAdminPage />
                  </RequireRole>
                ) : (
                  <Navigate to="/access-denied" replace />
                )
              }
            />
            {!analyticsEnabled ? (
              <Route path="apps/analytics/*" element={<Navigate to="/access-denied" replace />} />
            ) : null}
            <Route path="settings/profile" element={<UserProfilePage />} />
            <Route path="settings" element={<SettingsPage />}>
              <Route path="general" element={<SettingsGeneralPage />} />
              <Route path="my-apps" element={<AppStorePage />} />
              <Route path="editors" element={<SettingsEditorsPage />} />
              <Route path="storage" element={<SettingsStoragePage />} />
              <Route path="appearance" element={<SettingsAppearancePage />} />
              <Route path="support" element={<SettingsSupportPage />} />
              <Route path="about" element={<SettingsAboutPage />} />
            </Route>
            <Route
              path="settings/privacy"
              element={
                <LegalMarkdownPage markdownPath="/legal/privacy.md" title="Privacy Policy" />
              }
            />
            <Route
              path="settings/cookies"
              element={
                <LegalMarkdownPage markdownPath="/legal/cookie-policy.md" title="Cookie Policy" />
              }
            />
            <Route
              path="settings/terms"
              element={
                <LegalMarkdownPage markdownPath="/legal/terms-of-use.md" title="Terms of Use" />
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
